import { JSX, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

import { useGlobal } from '~/hooks/useGlobal';

import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs';
import { BreadcrumbsItem } from '../../../stores/CatalogStore';

import MobileHeaderLayout from '../../Main/MobileHeader/MobileHeaderLayout';
import MobileHeaderTitle from '../../Main/MobileHeader/MobileHeaderTitle';

import styles from '../Legals.module.scss';

import mdStyles from './LegalsHeimkaup.module.scss';

import '../styles.scss';

type Props = {
  handleLink: (anchor: string) => void;
};

const LEGALS_NAV = [
  {
    link: 'terms-and-conditions',
    title: 'Terms and Conditions',
  },
  {
    link: 'privacy-policy-main',
    title: 'Privacy Policy',
  },
];

const text = `
<h2 id="terms-and-conditions">Terms & Conditions</h2>

*By placing an order on the Website you accept these Terms and Conditions.*

1. ### Definitions

    - "Website" means heimkaup.is
    - "Seller" means Hjemmekob ApS
    - "Operator" means Heimkaup ehf., which operates the Website and customer-facing service in Iceland on behalf of the Seller
    - "Customer" or "you" means a natural person purchasing products on the Website for personal (non-business) use in Iceland

2. ### Seller and Operator

    **2.1 The products sold on the Website are sold by:**

    - Hjemmekob ApS
    - Company registration no.: 43277782
    - Registered address: Øresundsvej 48, 1. th., 2300 København S

    **2.2 The Website and customer service in Iceland are provided by:**

    - Heimkaup ehf.
    - Kennitala: 491219-0250
    - Smáratorg 3, 201 Kópavogur, Iceland

    **2.3** The service is aimed at private customers located in Iceland. The service is limited to delivery within Iceland.

3. ### Age limits and eligibility

    **3.1 Minimum age for purchases on the Website is as follows:**

    - At least 20 years old to purchase alcohol
    - At least 18 years old to purchase nicotine products

    **3.2** There is no legal minimum age for purchasing non-age-restricted products such as soft drinks and other non-alcoholic beverages, but the login and verification rules below may still apply to use of the Website.

    **3.3** We use Icelandic electronic ID (i. rafræn skilríki) for login and age verification where applicable.

    **3.4** Customers are responsible for ensuring that their login credentials, electronic ID and SMS codes are not shared with any other person, particularly anyone who does not meet the applicable age requirements.

    **3.5 It is not permitted to:**

    - Share your login credentials, electronic ID or SMS codes with any other person
    - Create multiple accounts for the purpose of abuse or circumvention of age limits or other restrictions
    - Use a consumer account for commercial resale of products

    **3.6** We reserve the right to suspend or close accounts and cancel orders in cases of misuse, breach of these Terms, attempted circumvention of age limits, or fraud.

4. ### Territory and product categories

    **4.1** Sales and delivery are restricted to within Iceland. We do not accept orders for delivery outside Iceland.

    **4.2** The Website focuses on the following product categories:

    - Alcoholic beverages (e.g., beer, wine, mixed drinks, seltzers, strong alcohol such as vodka, gin, rum, whisky)
    - Nicotine products (e.g., nicotine pouches, cigarettes, cigars)
    - Non-alcoholic beverages (e.g., soft drinks, sodas, energy drinks, tonics)

    **4.3** Certain products may be subject to additional legal restrictions or local rules. We will maintain compliance with Icelandic law in relation to all products sold.

5. ### Ordering and contract formation

    **5.1 To place an order, you must:**

    - Log in with your electronic ID
    - Select the products you wish to purchase
    - Provide accurate delivery details
    - Complete payment

    **5.2** An order placed on the Website constitutes an offer to purchase. A binding contract is formed when you receive an order confirmation from us (e.g., by email or SMS).

    **5.3** You are responsible for checking your order confirmation and ensuring that all information is correct, including your delivery address and contact details.

    **5.4** We reserve the right to cancel or refuse an order, including in the following circumstances:

    - Suspected fraud or misuse
    - Breach of age limits or other legal requirements
    - Unavailability of products
    - Obvious errors in price or product information

    If an order is canceled after payment has been made, we will issue a refund for the affected items.

6. ### Prices and payment

    **6.1** All prices are in Icelandic krónur (ISK) and include Icelandic VAT.

    **6.2** Delivery charges are shown at checkout.

    **6.3** We accept all major payment cards and supported digital wallets.

    **6.4** Card and digital wallet payments are processed securely via Verifone. We do not store your full card number or security codes on our systems.

    **6.5** Your payment method will normally be charged upon order confirmation, unless otherwise stated.

    **6.6** Prices may change from time to time. The price applicable to your order is the price shown at the time you place your order, unless there is a clear pricing error.

7. ### Delivery and age checks at delivery

    **7.1** Delivery is carried out by Wolt and/or other professional delivery partners. Delivery options and fees are shown at checkout.

    **7.2** For orders containing alcohol or nicotine products, Wolt or the delivery partner is responsible for checking your name and date of birth against valid identification at delivery. Delivery may be refused if you cannot prove your age or if the courier has reason to suspect that the products will be supplied to an underage person.

    **7.3** If no one is available at the delivery address or age verification fails, the delivery partner may refuse delivery and return the products, or follow specific allowed instructions (e.g., pick-up point) compatible with age-verification rules.

    **7.4** If an item is unavailable due to a stock error, we will contact you and offer a replacement or refund.

    **7.5** Risk of loss or damage to products passes to you upon delivery. Ownership passes to you upon full payment.

8. ### Right of withdrawal and returns

    **8.1** You may return most products within 14 days of delivery, provided they are in the same condition as delivered and include original packaging.

    **8.2** Food products (beer, wine, etc.) cannot be returned except under specific agreements, e.g., for large event purchases.

    **8.3** The right of withdrawal does not limit your rights in relation to defective, damaged or incorrect products, which are handled under section 9 and consumer law.

    **8.4** To exercise your right of withdrawal, contact customer service within 14 days of delivery, provide proof of purchase, and you may be required to cover the cost of returning the products.

    **8.5** Refunds for returned products will be issued within a reasonable time using the original payment method, unless otherwise agreed.

9. ### Defective, damaged or incorrect products

    **9.1** If you receive a product that is defective, damaged, expired, or incorrect, please contact customer service immediately.

    **9.2** Remedies available in line with Icelandic consumer rules include replacement, price reduction, or cancellation and refund.

    **9.3** We may request photos or other documentation, and products may need to be returned for inspection.

    **9.4** This section does not limit your rights under the Icelandic Consumer Sales Act No. 48/2003.

10. ### Accounts, security and misuse

    **10.1** You are responsible for maintaining the confidentiality of your account credentials and restricting access to your account and devices.

    **10.2** You are liable for activity on your account unless you report unauthorized use without undue delay.

    **10.3** You must notify us immediately if you suspect unauthorized use of your account, loss of access, or other security issues.

    **10.4** We reserve the right to take action (suspension, closure, blocking access) in cases of misuse, fraud, or breach of these Terms.

11. ### Personal data and privacy

    **11.1** We process personal data in connection with your use of the Website and purchases. Personal data is treated confidentially in accordance with the Icelandic Data Protection Act No. 90/2018.

    **11.2** For more detailed information on how we process personal data, your rights, and who to contact for privacy matters, please see our Privacy Notice on the Website.

    **11.3** By using the Website and placing orders, you acknowledge that you have had the opportunity to read our Privacy Notice.

12. ### Governing law and disputes

    **12.1** These Terms and any disputes related to them are governed by Icelandic law.

    **12.2** We encourage you to contact customer service to resolve any complaints.

    **12.3** Consumers may bring disputes before relevant Icelandic consumer dispute bodies.

    **12.4** Any disputes that cannot be resolved will be brought before the District Court of Reykjavík (Héraðsdómur Reykjavíkur).

13. ### Contact details

    - General and customer support: heimkaup@heimkaup.is
    - Privacy and data protection: personuvernd@heimkaup.is

14. ### Severability

    **14.1** If any provision of these Terms is found to be unlawful, void, or unenforceable, that provision will be severed, and the remaining provisions will remain in full force and effect.

<h2 id="privacy-policy-main">Privacy Notice</h2>

1. ### Controller

    Heimkaup ehf., kt. 491219-0250, Smáratorg 3, 201 Kópavogur, is the controller for personal data processed on heimkaup.is.
    Email: personuvernd@heimkaup.is

2. ### What data we collect

    We process identification data, contact details, login and age-verification data, order and delivery information, payment references, communication data and technical website usage data.

3. ### Why we process your data

    We use your data to operate your account, process and deliver orders, fulfil legal obligations (including age verification for alcohol and nicotine), prevent misuse and fraud, provide customer service, maintain website security and, if you consent, send marketing messages.

4. ### Legal bases

    We process data based on performance of a contract (orders and deliveries), legal obligations (alcohol and nicotine age rules, accounting), and our legitimate interests (security and service improvement). Marketing is based on your consent where required.

5. ### Sharing your data

    We share data with payment providers (e.g. Verifone), delivery partners (e.g. Wolt), IT service providers and authorities when required by law. We do not sell personal data. Card details are not stored by us.

6. ### International transfers

    If data is transferred outside the EEA, we use lawful safeguards such as Standard Contractual Clauses.

7. ### Retention

    We keep data only as long as needed to provide the service or comply with legal obligations.

8. ### Your rights

    You have the right to access, correct, delete, restrict, or object to processing, and to withdraw consent. Otherwise, your rights are subject to the Heimkaup privacy policy (https://www.heimkaup.is/personuvernd).
    You may contact us at personuvernd@heimkaup.is.

**Last updated: 06.01.2026**
`;

const LegalsHeimkaup = ({ handleLink }: Props): JSX.Element => {
  const { t } = useTranslation();
  const { isTablet } = useGlobal();

  useEffect(() => {
    setTimeout(() => {
      const anchor = window.location.hash.slice(1);
      handleLink(anchor);
    }, 0);
  }, []);

  return (
    <>
      <Helmet>
        <meta data-react-helmet="true" name="robots" content="index, follow" />
      </Helmet>
      <MobileHeaderLayout content={<MobileHeaderTitle text={t('legals')} />} />
      <div className="content-layout legals">
        <div className={styles.root}>
          {!isTablet && (
            <>
              <Breadcrumbs
                items={
                  [
                    {
                      text: t('home'),
                      link: '/',
                    },
                    {
                      text: t('legals'),
                      link: '/legals',
                    },
                  ].filter(Boolean) as BreadcrumbsItem[]
                }
              />

              <h2 className={styles.title}>{t('legals')}</h2>
            </>
          )}

          <div className={styles.page}>
            <div className={styles.page__links_container}>
              <ul className={styles.page__links}>
                {LEGALS_NAV.map((i) => (
                  <li className={styles.page__links_item} key={i.link}>
                    <a
                      href={`#${i.link}`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleLink(i.link);
                      }}
                    >
                      {i.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className={styles.page__content}>
              <ReactMarkdown
                className={mdStyles.mdStyles}
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
              >
                {text}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LegalsHeimkaup;
